import axios from "axios";
import { cacheGet, cacheSet } from "@/lib/offlineDB";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Sinal fraco: GET espera no máximo 12s; POST/PUT (com fotos) até 90s. Sem isso o app "congela" até o TCP desistir.
const GET_TIMEOUT_MS = 12000;
const WRITE_TIMEOUT_MS = 90000;
// Se já existe cache e a rede demora mais que isto, mostra o cache e segue atualizando em segundo plano
const STALE_IF_SLOW_MS = 4000;

const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pcp_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (!config.timeout) config.timeout = (config.method || "get").toLowerCase() === "get" ? GET_TIMEOUT_MS : WRITE_TIMEOUT_MS;
  return config;
});

// Chave do cache offline: caminho + query (sem baseURL)
const cacheKeyOf = (config) => {
  if (!config || (config.method || "get").toLowerCase() !== "get") return null;
  if (config.responseType && config.responseType !== "json") return null;
  const url = config.url || "";
  const qs = config.params ? "?" + new URLSearchParams(config.params).toString() : "";
  return url.replace(API, "") + qs;
};

const isNetworkError = (err) => !err.response && (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED" || err.code === "ETIMEDOUT" || !navigator.onLine);

const fromCache = (config, hit) => ({ data: hit.data, status: 200, statusText: "OK (offline)", headers: {}, config, fromCache: true, cachedAt: hit.cached_at });

// Adapter: offline → responde do cache na hora (sem tentar rede); online com cache → rede com teto de 2,5s, senão cache
const httpAdapter = axios.getAdapter(axios.defaults.adapter);
api.defaults.adapter = async (config) => {
  const key = cacheKeyOf(config);
  if (!key) return httpAdapter(config);
  if (!navigator.onLine) {
    const hit = await cacheGet(key).catch(() => null);
    if (hit) return fromCache(config, hit);
    const e = new axios.AxiosError("Sem internet e sem cache", "ERR_NETWORK", config); e.config = config; throw e;
  }
  const network = httpAdapter(config).then((r) => { if (r.status === 200) cacheSet(key, r.data).catch(() => null); return r; });
  const hit = await cacheGet(key).catch(() => null);
  if (!hit) return network;
  // Só erro de REDE cai no cache; erro HTTP (4xx/5xx) é propagado — nunca mascarar erro do servidor com dado antigo
  return Promise.race([
    network.catch((e) => { if (isNetworkError(e)) return fromCache(config, hit); throw e; }),
    new Promise((res) => setTimeout(() => res(fromCache(config, hit)), STALE_IF_SLOW_MS)),
  ]);
};

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const key = cacheKeyOf(err.config);
    if (key && isNetworkError(err)) {
      const hit = await cacheGet(key).catch(() => null);
      if (hit) {
        return { data: hit.data, status: 200, statusText: "OK (offline)", headers: {}, config: err.config, fromCache: true, cachedAt: hit.cached_at };
      }
    }
    if (err.response?.status === 401) {
      const path = window.location.pathname;
      if (path !== "/login") {
        localStorage.removeItem("pcp_token");
        localStorage.removeItem("pcp_user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Ocorreu um erro. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default api;
